"use client";

import * as React from "react";
import type { Table } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Button, cn } from "@aio/platform-ui";

/**
 * Props for {@link BulkActionBar}.
 */
export interface BulkActionBarProps<TData> {
  /** The TanStack table whose `rowSelection` drives visibility. */
  table: Table<TData>;
  /**
   * Caller-defined actions rendered to the right of the count. Typically a
   * row of `<Button>`s; the bar itself adds a "Clear selection" affordance
   * after these actions.
   */
  actions?: React.ReactNode;
  /**
   * Override the clear-selection behaviour. Defaults to
   * `table.resetRowSelection()`. The handler runs *in addition* to the
   * default reset, but the default is suppressed when an override is given —
   * use {@link onAfterClear} if you need both.
   */
  onClear?: () => void;
  /** Hook fired after the default clear runs (when no `onClear` is given). */
  onAfterClear?: () => void;
  /** Override the rendered count copy. Defaults to "{n} selected". */
  formatCount?: (count: number) => string;
  /** Extra classes for the wrapping element. */
  className?: string;
}

/** Default "{n} selected" copy. Singular for one row, plural otherwise. */
function defaultCountLabel(count: number): string {
  return `${count} selected`;
}

/**
 * Sticky-bottom panel that appears when at least one row is selected.
 *
 * Layout (from left to right):
 *   [ "{N} selected" count ] [ caller actions ] [ Clear selection ]
 *
 * Behaviour:
 * - Hidden when zero rows are selected — returns `null` outright so it
 *   does not occupy layout space.
 * - "Clear selection" calls `table.resetRowSelection()` by default. Pass
 *   `onClear` to take over completely (e.g. when selection lives outside
 *   TanStack), or `onAfterClear` to fire follow-on logic.
 * - The bar is sticky to the bottom of the closest scrollable ancestor
 *   (`position: sticky; bottom: 0`) so it remains in view inside long
 *   tables, and centred horizontally with a max-width on larger screens.
 */
export function BulkActionBar<TData>({
  table,
  actions,
  onClear,
  onAfterClear,
  formatCount,
  className,
}: BulkActionBarProps<TData>): React.JSX.Element | null {
  const selected = table.getState().rowSelection;
  const count = Object.keys(selected).filter((k) => selected[k]).length;

  const handleClear = React.useCallback(() => {
    if (onClear) {
      onClear();
      return;
    }
    table.resetRowSelection();
    onAfterClear?.();
  }, [onClear, onAfterClear, table]);

  if (count === 0) return null;

  const countLabel = (formatCount ?? defaultCountLabel)(count);

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      // sticky to the closest scrollable ancestor; pinned to the bottom of
      // the viewport on mobile via the parent flex layout.
      className={cn(
        "sticky bottom-3 z-20 mx-auto flex w-full max-w-3xl items-center justify-between gap-3",
        "rounded-lg border border-border-strong bg-bg-elevated px-4 py-2.5 shadow-lg",
        className,
      )}
      data-testid="bulk-action-bar"
    >
      <span
        className="text-sm font-medium text-fg"
        data-testid="bulk-action-count"
        aria-live="polite"
      >
        {countLabel}
      </span>
      <div className="flex items-center gap-2">
        {actions}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="gap-1.5"
          data-testid="bulk-action-clear"
        >
          <X className="size-4" aria-hidden="true" />
          <span>Clear selection</span>
        </Button>
      </div>
    </div>
  );
}
